export const DENSITY = {
    compact: { row: 28, bodyAdd: 0 },
    comfortable: { row: 32, bodyAdd: 0 },
    table: { row: 40, bodyAdd: 2 },
};
/**
 * The app's density (UI r3c, the GM's request of 2026-10-14, DECISIONS 499):
 * Table, as the design kit shows it. The app root and the `/design` switcher
 * start here.
 */
export const DEFAULT_DENSITY = 'table';
/**
 * `--cx-page-scale`: a density's body size over Comfortable's, so page caps
 * written as `calc(<cap> * var(--cx-page-scale))` stay exact at Comfortable
 * and widen with the text at Table (UI r3c deliverable 44).
 */
export function pageScale(body, density) {
    return (body + DENSITY[density].bodyAdd) / (body + DENSITY.comfortable.bodyAdd);
}
/** Durations, ms. Only opacity and transform animate. */
export const MOTION = { fast: 120, medium: 180, slow: 240 };
export const MOTION_PROPERTIES = ['opacity', 'transform'];
export const LAYOUT = {
    sidebar: 280,
    sidebarMin: 240,
    sidebarMax: 360,
    rail: 56,
    header: 48,
    measureMinCh: 66,
    measureMaxCh: 72,
    rightRail: 320,
    sidePeek: 520,
    splitPaneMin: 420,
    /** Below this viewport width a split view becomes two tabs. */
    splitTabsBelow: 1100,
};
/**
 * Spacing, px, on Tailwind's 4 px scale with its half steps (DECISIONS 74
 * item 4): `--cx-space-<step>`, `0-5` for 0.5. Vanilla views (the editor's
 * overlay field, menu rows, chips) write these instead of the kit's pixels.
 */
export const SPACE = {
    '0-5': 2,
    '1': 4,
    '1-5': 6,
    '2': 8,
    '2-5': 10,
    '3': 12,
    '4': 16,
    '5': 20,
    '6': 24,
    '8': 32,
};
/** Control heights, px: `--cx-control-h-<size>` (small buttons, buttons and menu rows, inputs). */
export const CONTROL_HEIGHT = { sm: 28, md: 32, lg: 36 };
export const FOCUS_RING = { width: 2, offset: 2, token: 'accent' };
/** Tint levels, as fractions. Block tints are 8 %; type-color chip tints are at most 12 %. */
export const TINT = {
    block: 0.08,
    chipMax: 0.12,
    /** The level at which a hue is checked on its own tint (acceptance 1). */
    hueCheck: 0.14,
};
/**
 * Minimum pairwise ΔE of the thirteen hued types (amendments 2.1 section 7).
 * A color change may never lower one of these figures. A hued user type
 * (Grimoire Noir's Deity) joins the set and is held to the same floors.
 */
export const HUE_FLOORS = {
    atlas: {
        dark: { normal: 5.4, protan: 5.4, deutan: 4.7, tritan: 3.2 },
        light: { normal: 5.3, protan: 4.9, deutan: 4.7, tritan: 3.8 },
    },
    ledger: {
        dark: { normal: 6.9, protan: 6.2, deutan: 6.0, tritan: 4.3 },
        light: { normal: 5.3, protan: 4.2, deutan: 4.2, tritan: 3.5 },
    },
    noir: {
        dark: { normal: 7.1, protan: 6.9, deutan: 6.7, tritan: 4.8 },
        light: { normal: 5.9, protan: 4.2, deutan: 4.0, tritan: 4.6 },
    },
};
/** Design floors of acceptance criterion 1, 2 and 4 (WCAG ones are compared unrounded). */
export const FLOORS = {
    text: 4.5,
    mutedOnBlockTint: 4.6,
    hueOnSurface: 4.5,
    hueOnOwnTint: 4.6,
    noteOnSurface: 5.3,
    borderStrong: 1.5,
    accentContrast: 4.5,
    nonText: 3.0,
    accentToHue: 5.0,
    ledgerAccentToMuted: 12.0,
    /** A hued user type (Deity) against `textMuted` and `text`, normal vision. */
    userTypeToNeutral: 5.0,
};
/**
 * Floors of the Notion palette (UI r1 deliverable 2). 4.5 is WCAG AA and 3.0
 * WCAG non-text (compared unrounded); 4.6 is the critic's headroom (shared fix
 * 4); 5.0 is the critic's "unreliable under 5" and the gate's accent rule; 2.0
 * under simulation is "indistinguishable under 2". Design floors are compared
 * rounded half up to one decimal place.
 */
export const PALETTE_FLOORS = {
    text: 4.5,
    bodyOnBackground: 4.6,
    own: 4.6,
    cross: 4.5,
    accentOnBackground: 4.5,
    iconOnBackground: 3.0,
    min: { normal: 5.0, protan: 2.0, deutan: 2.0, tritan: 2.0 },
    vsText: 5.0,
    accentToNearest: 5.0,
    backgroundVisible: 2.0,
    backgroundMin: 2.0,
};
/** Fixes applied to every direction (amendments 2.1 section 7, mandatory fixes 1–11). */
export const SHARED_FIXES = [
    "The critic's replacement type-color map, with spell added and note kept neutral",
    'A border-strong token; light-mode raised surfaces carry a shadow and border-strong',
    'Type colors reach 4.6 on their own tint; chip tints are capped at 12 %; text-muted never sits on a type tint',
    'Corrected dark text-muted, the critic’s success colors',
    'Dashed borders only on AI drafts; secrets have a solid border and the eye-off icon',
    'Fourteen type glyphs, and one for the user type Deity, drawn on a 16 px grid; SRD entries reuse the canon glyph with an SRD badge',
    'GM notes inline, in text color, at full size',
    'The right rail collapses before the document column drops below its measure (66–72ch)',
    'Chips are buttons: click peeks, Mod-click opens the page, Alt-click a center peek, Shift-click a split; Esc closes one layer',
    'Chips are 22 px tall, spaced so each keeps a clear 24 px target circle (WCAG 2.5.8)',
    'A document already open in one pane opens read-only in the other, with Edit here',
];
//# sourceMappingURL=shared.js.map